/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useEffect, useMemo, useState } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { TagApi } from "../../api/Tags";
import { updateUser } from "../../api/User";
import { SettingsHelpVideosAtom } from "../../atoms/SettingsHelpVideos";
import { TagsAtom } from "../../atoms/TagsAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { HelpVideoNameMap, IHelpContexts } from "../../types/HelpVideosTypes";
import { ITag } from "../../types/TagTypes";
import { localStorageGetItem } from "../../utils/LocalStorage";
import ChangePassword from "../Modal/container/ChangePassword";
import { useModal } from "../Modal/Modal";
import StyledButton from "../StyledButton";
import TagSelect from "../TagSelect/TagSelect";
const Container = styled.div`
  max-width: 1200px;
  width: 100%;
  height: fit-content;

  margin: 40px 0px;
  padding: 48px;

  border-radius: 4px;
  border: 1px solid #eaeaeabf;
  background-color: #ffffff;

  cursor: default;

  @media screen and (max-width: 1140px) {
    padding: 48px 24px;
  }

  .envmntf11 {
    margin-top: 8px !important;
    margin-bottom: 0 !important;
    grid-gap: 8px !important;
  }

  .e14mssdu3 {
    margin: 0 !important;
  }
`;

const MainTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 18px;
`;

const SecondaryTitle = styled.div`
  color: #222222;
  font-weight: 600;
  font-size: 16px;

  display: flex;
`;

const MainText = styled.div`
  color: #707070;
  font-weight: 400;
  font-size: 15px;
`;

const TextWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;

  grid-gap: 8px;

  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 24px;
  margin-bottom: 24px;

  border-top: 1px solid #eaeaeabf;
  padding-top: 24px;
  margin-top: 24px;

  &:first-of-type {
    border-top: unset;
    padding-top: unset;
    margin-top: unset;
  }
`;

const FormContent = styled.div`
  width: 100%;
  height: fit-content;

  padding: 0 14 px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  grid-gap: 24px;

  .divider {
    width: 100%;
    height: 1px;
    background-color: #eaeaeabf;

    margin: 24px 0;
  }
`;

const SpaceBetween = styled.div`
  width: 100%;
  min-height: 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  @media screen and (max-width: 1140px) {
    grid-gap: 32px;
  }

  .flex-column-wrapper {
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;

    grid-gap: 8px;
  }
`;

export default function Security(): JSX.Element | null {
  const [user, setUser] = useRecoilState(UserAtom);
  const [tags, setTags] = useRecoilState(TagsAtom);
  const modal = useModal();

  const [protectionTags, setProtectionTags] = useState<ITag[]>([]);

  const setHelpVideo = useSetRecoilState(SettingsHelpVideosAtom);

  const canShowChangePassword = useMemo(() => {
    let internalUid = localStorageGetItem("INTERNAL_USER_UID") as string;

    if (internalUid) {
      return true;
    }

    if (user.authType === "LEARNISTIC") {
      return false;
    }

    return true;
  }, []);

  useEffect(() => {
    setHelpVideo({
      helpTitle: HelpVideoNameMap[IHelpContexts.SettingsSecurity],
      helpContext: IHelpContexts.SettingsSecurity,
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      let userTags = await TagApi.readAll({ getCampaigns: true });
      setTags(userTags);
    })();
  }, [user]);

  useEffect(() => {
    const userProptectedTags = user.options.protectedTags || [];
    const mappedProtectionTags = userProptectedTags
      .map((tag) => tags.find((t) => t._id === tag))
      .filter((t) => !!t) as ITag[];

    setProtectionTags(mappedProtectionTags);
  }, [tags]);

  async function handleTwoStepVerification(toggle: boolean) {
    if (!user) return false;

    const userObject = {
      ...user,
      twoStepVerification: toggle,
    };

    delete (userObject as any)._id;
    const success = await updateUser(userObject);

    if (success) {
      setUser({
        ...user,
        options: {
          ...user.options,
          twoStepVerifiation: toggle as boolean,
        },
      });
    }
    return false;
  }

  async function handleProtectionTags(tags: ITag[]) {
    if (!user) return false;

    const userObject = {
      ...user,
      options: {
        ...user.options,
        protectedTags: tags.map((t) => t._id),
      },
    };

    delete (userObject as any)._id;
    const success = await updateUser(userObject);

    if (success) {
      setUser({
        ...user,
        options: {
          ...user.options,
          protectedTags: tags.map((t) => t._id),
        },
      });
      setProtectionTags(tags);
    }
    return false;
  }

  if (!user) return null;
  return (
    <Container>
      <TextWrapper>
        <MainTitle>Security</MainTitle>
        <MainText>
          Security settings & options to customize your app behavior.
        </MainText>
      </TextWrapper>

      <FormContent>
        {canShowChangePassword && (
          <SpaceBetween>
            <div className="flex-column-wrapper">
              <SecondaryTitle>Change your passcode</SecondaryTitle>
              <MainText>
                Set a unique passcode to protect your personal Berserker
                account.
              </MainText>
            </div>

            <StyledButton
              css={css`
                min-width: 162px;
                max-width: 162px;
              `}
              inverted
              disable={!canShowChangePassword}
              onClick={() => {
                modal.pushModal(<ChangePassword />, "Change Your Passcode");
              }}
            >
              Change passcode
            </StyledButton>
          </SpaceBetween>
        )}
        <SpaceBetween
          css={css`
            align-items: flex-start;
          `}
        >
          <div className="flex-column-wrapper">
            <SecondaryTitle>Contact protection tags</SecondaryTitle>
            <MainText>
              Select tags to prevent your contacts from being deleted.
            </MainText>
          </div>

          <div
            css={css`
              width: 400px;
              height: auto;
            `}
          >
            <TagSelect
              tags={protectionTags}
              preserveSystemTags={true}
              handleChoseTags={(tags) => handleProtectionTags(tags)}
            />
          </div>
        </SpaceBetween>
      </FormContent>
    </Container>
  );
}
